import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import DateField from "./DateField";
import { useT } from "@/lib/i18n";

export default function EditTripDialog({ open, trip, onSave, onClose, defaultYear }) {
  const { t } = useT();
  const [entry, setEntry] = useState(null);
  const [exit, setExit] = useState(null);
  const [openTrip, setOpenTrip] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && trip) {
      setEntry(trip.entry);
      setExit(trip.exit || null);
      setOpenTrip(!trip.exit);
    }
  }, [open, trip]);

  const handleSave = async () => {
    if (!entry) {
      toast.error(t("add.needEntry"));
      return;
    }
    if (!openTrip && !exit) {
      toast.error(t("add.needExit"));
      return;
    }
    if (!openTrip && exit && exit < entry) {
      toast.error(t("add.exitBeforeEntry"));
      return;
    }
    setBusy(true);
    try {
      await onSave(entry, openTrip ? null : exit);
      toast.success(t("edit.saved"));
      onClose();
    } catch (err) {
      toast.error(err.message || "Greška pri čuvanju");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#131620] border-[#272A35] text-slate-100"
        data-testid="edit-trip-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl tracking-tight">
            {t("edit.title")}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm mt-1.5">
            {t("edit.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
              {t("add.entry")}
            </Label>
            <DateField
              value={entry}
              onChange={setEntry}
              testId="edit-entry"
              defaultYear={defaultYear}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
              {t("add.exit")}
            </Label>
            <div className={openTrip ? "opacity-40 pointer-events-none" : ""}>
              <DateField
                value={exit}
                onChange={setExit}
                testId="edit-exit"
                minDate={entry}
                defaultYear={defaultYear}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="edit-open"
              checked={openTrip}
              onCheckedChange={setOpenTrip}
              data-testid="edit-open-switch"
            />
            <Label
              htmlFor="edit-open"
              className="text-sm text-slate-300 cursor-pointer"
            >
              {t("edit.openTrip")}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="bg-[#1A1E2C] border-[#363A49] hover:bg-[#272A35] text-slate-100"
            data-testid="edit-cancel"
          >
            {t("edit.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
            data-testid="edit-save"
          >
            {busy ? t("edit.saving") : t("edit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
